import { useEffect, useMemo } from "react";
import { useListData } from "react-stately";
import { TableCard } from "@/components/application/table/table";
import { Tab, TabList, Tabs } from "@/components/application/tabs/tabs";
import { MultiSelect } from "@/components/base/select/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { ComparisonPreset, TimeMode, TrendGranularity, WeekStartDay } from "../flag-qa-types";

const QUICK_RANGES = [
  { value: "custom", label: "Custom" },
  { value: "3", label: "Last 3 days" },
  { value: "7", label: "Last 7 days" },
  { value: "15", label: "Last 15 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "all", label: "All" },
] as const;

const TIME_MODE_TABS: { id: string; label: string }[] = [
  { id: "single", label: "Single Period" },
  { id: "compare", label: "Compare" },
  { id: "trend", label: "Trend" },
  { id: "risks", label: "Risks Needing Attention" },
];

const COMPARISON_PRESETS = [
  { value: "wow", label: "Week over Week" },
  { value: "mom", label: "Month over Month" },
  { value: "custom", label: "Custom" },
] as const;

const TREND_GRANULARITIES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
] as const;

const WEEK_START_OPTIONS = [
  { value: "MON", label: "Monday" },
  { value: "SUN", label: "Sunday" },
] as const;

type MerchantItem = { id: string; label: string };

interface FlagQaFiltersProps {
  // Merchants
  merchants: string[];
  selectedMerchants: string[];
  onSelectedMerchantsChange: (merchants: string[]) => void;

  // Time mode
  timeMode: TimeMode;
  onTimeModeChange: (mode: TimeMode) => void;

  // Single period
  quickRange: string;
  onQuickRangeChange: (value: string) => void;
  startDate?: string;
  endDate?: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;

  // Compare mode
  comparisonPreset: ComparisonPreset;
  onComparisonPresetChange: (preset: ComparisonPreset) => void;
  periodAStart?: string;
  periodAEnd?: string;
  periodBStart?: string;
  periodBEnd?: string;
  onPeriodAStartChange: (value: string) => void;
  onPeriodAEndChange: (value: string) => void;
  onPeriodBStartChange: (value: string) => void;
  onPeriodBEndChange: (value: string) => void;

  // Trend mode
  trendGranularity: TrendGranularity;
  onTrendGranularityChange: (granularity: TrendGranularity) => void;
  trendStartDate?: string;
  trendEndDate?: string;
  onTrendStartDateChange: (value: string) => void;
  onTrendEndDateChange: (value: string) => void;
  weekStartDay: WeekStartDay;
  onWeekStartDayChange: (day: WeekStartDay) => void;
}

export function FlagQaFilters({
  merchants,
  selectedMerchants,
  onSelectedMerchantsChange,
  timeMode,
  onTimeModeChange,
  quickRange,
  onQuickRangeChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  comparisonPreset,
  onComparisonPresetChange,
  periodAStart,
  periodAEnd,
  periodBStart,
  periodBEnd,
  onPeriodAStartChange,
  onPeriodAEndChange,
  onPeriodBStartChange,
  onPeriodBEndChange,
  trendGranularity,
  onTrendGranularityChange,
  trendStartDate,
  trendEndDate,
  onTrendStartDateChange,
  onTrendEndDateChange,
  weekStartDay,
  onWeekStartDayChange,
}: FlagQaFiltersProps) {
  const merchantItems: MerchantItem[] = useMemo(() => merchants.map((m) => ({ id: m, label: m })), [merchants]);

  const selectedMerchantItems = useListData<MerchantItem>({
    initialItems: selectedMerchants.map((m) => ({ id: m, label: m })),
  });

  // Sync external state with internal list
  // biome-ignore lint/correctness/useExhaustiveDependencies: useListData returns stable references
  useEffect(() => {
    const currentIds = selectedMerchantItems.items.map((i) => i.id);
    const newIds = selectedMerchants;

    // Add missing items
    for (const id of newIds) {
      if (!currentIds.includes(id)) {
        selectedMerchantItems.append({ id, label: id });
      }
    }

    // Remove extra items
    for (const id of currentIds) {
      if (!newIds.includes(id)) {
        selectedMerchantItems.remove(id);
      }
    }
  }, [selectedMerchants]);

  const handleMerchantInserted = (key: React.Key) => {
    const newMerchants = [...selectedMerchants, key as string];
    onSelectedMerchantsChange(newMerchants);
  };

  const handleMerchantCleared = (key: React.Key) => {
    const newMerchants = selectedMerchants.filter((m) => m !== key);
    onSelectedMerchantsChange(newMerchants);
  };

  return (
    <TableCard.Root className="p-4">
      <div className="flex flex-col gap-4">
        {/* Row 1: Merchant multi-select and Time Mode tabs */}
        <div className="flex gap-4 items-end">
          <div className="flex-1 max-w-md">
            <label className="text-sm font-medium mb-2 block text-secondary">Merchants</label>
            <MultiSelect
              items={merchantItems}
              selectedItems={selectedMerchantItems}
              onItemInserted={handleMerchantInserted}
              onItemCleared={handleMerchantCleared}
              placeholder={selectedMerchants.length === 0 ? "All merchants" : "Search merchants..."}
              size="sm"
            >
              {(item) => <MultiSelect.Item id={item.id} textValue={item.label} label={item.label} />}
            </MultiSelect>
          </div>

          <div className="flex-1">
            <Tabs selectedKey={timeMode} onSelectionChange={(key) => onTimeModeChange(key as TimeMode)}>
              <TabList items={TIME_MODE_TABS} type="button-gray" size="sm">
                {(item) => <Tab id={item.id} label={item.label} />}
              </TabList>
            </Tabs>
          </div>
        </div>

        {/* Row 2: Time-specific controls based on mode */}
        {(timeMode === "single" || timeMode === "risks") && (
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block text-secondary">Date Range</label>
              <Select value={quickRange} onValueChange={onQuickRangeChange}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  {QUICK_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value} className="text-white focus:bg-gray-700">
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {quickRange === "custom" && (
              <>
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block text-secondary">Start Date</label>
                  <input
                    type="date"
                    value={startDate || ""}
                    onChange={(e) => onStartDateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-700 rounded-md bg-gray-800 text-primary"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block text-secondary">End Date</label>
                  <input
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-700 rounded-md bg-gray-800 text-primary"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {timeMode === "compare" && (
          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block text-secondary">Comparison</label>
                <Select value={comparisonPreset} onValueChange={(v) => onComparisonPresetChange(v as ComparisonPreset)}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {COMPARISON_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value} className="text-white focus:bg-gray-700">
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {comparisonPreset === "custom" && (
              <div className="flex gap-4">
                <div className="flex-1 p-3 rounded-lg bg-gray-800/50">
                  <div className="text-xs font-medium text-tertiary mb-2">Period A (Current)</div>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={periodAStart || ""}
                      onChange={(e) => onPeriodAStartChange(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-700 rounded bg-gray-800 text-primary text-sm"
                    />
                    <span className="text-tertiary self-center">to</span>
                    <input
                      type="date"
                      value={periodAEnd || ""}
                      onChange={(e) => onPeriodAEndChange(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-700 rounded bg-gray-800 text-primary text-sm"
                    />
                  </div>
                </div>
                <div className="flex-1 p-3 rounded-lg bg-gray-800/50">
                  <div className="text-xs font-medium text-tertiary mb-2">Period B (Previous)</div>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={periodBStart || ""}
                      onChange={(e) => onPeriodBStartChange(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-700 rounded bg-gray-800 text-primary text-sm"
                    />
                    <span className="text-tertiary self-center">to</span>
                    <input
                      type="date"
                      value={periodBEnd || ""}
                      onChange={(e) => onPeriodBEndChange(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-700 rounded bg-gray-800 text-primary text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {timeMode === "trend" && (
          <div className="flex gap-4">
            <div className="w-40">
              <label className="text-sm font-medium mb-2 block text-secondary">Granularity</label>
              <Select value={trendGranularity} onValueChange={(v) => onTrendGranularityChange(v as TrendGranularity)}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 text-white">
                  {TREND_GRANULARITIES.map((g) => (
                    <SelectItem key={g.value} value={g.value} className="text-white focus:bg-gray-700">
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {trendGranularity === "weekly" && (
              <div className="w-40">
                <label className="text-sm font-medium mb-2 block text-secondary">Week starts on</label>
                <Select value={weekStartDay} onValueChange={(v) => onWeekStartDayChange(v as WeekStartDay)}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    {WEEK_START_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white focus:bg-gray-700">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block text-secondary">Start Date</label>
              <input
                type="date"
                value={trendStartDate || ""}
                onChange={(e) => onTrendStartDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-700 rounded-md bg-gray-800 text-primary"
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block text-secondary">End Date</label>
              <input
                type="date"
                value={trendEndDate || ""}
                onChange={(e) => onTrendEndDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-700 rounded-md bg-gray-800 text-primary"
              />
            </div>
          </div>
        )}
      </div>
    </TableCard.Root>
  );
}
